import { Card, Col, Row, Modal, Button } from 'react-bootstrap'
import { useLocation, useHistory } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import FormWizard, { Step } from '../../../components/organisms/FormWizard'
import Step1, { ICreateCompanyFormStep1 } from './Step1/step1'
import React, { useState, useContext } from 'react'
// Step2 removed - users should be managed via Settings > User Management instead
import Step4 from './Step4/step4'
import { Title } from '../../../components/atoms/Title'
import { SetableContext } from '../../../interfaces/setable-context'
import {
  CompanyReturnedFromCreation,
  CompanyType
} from '../../../domain/models/CompanyModel'
import { createCompany, editCompany } from '../../../domain/usecases/Company'
import Step3, { ICreateCompanyFormStep3 } from './Step3/step3'
import { AppContext } from '../../../app'

export type ICreateCompanyForm = Partial<
  ICreateCompanyFormStep1 & ICreateCompanyFormStep3
>

export const CreateCompanyContext = React.createContext<
  SetableContext<ICreateCompanyForm>
>({})

type stateRouteType = {
  company: CompanyType
}

type Props = {
  handleCreateCompany: createCompany
  handleUpdateCompany: editCompany
}

export default function CreateCompany({
  handleCreateCompany,
  handleUpdateCompany
}: Props) {
  const { addToast } = useToasts()
  const history = useHistory()
  const { state } = useLocation<stateRouteType>()
  const { user, selectContext } = useContext(AppContext)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [showContextModal, setShowContextModal] = useState(false)
  const [createdCompanyId, setCreatedCompanyId] = useState<string | null>(null)
  const company = state?.company
    ? {
        id: state.company.id,
        cnpj: state.company.cnpj,
        business_name: state.company.business_name,
        proxies: state.company.proxies
      }
    : {}

  const [data, setData] = useState<ICreateCompanyForm>(company)

  const steps: Step[] = [
    {
      id: 1,
      label: 'Dados da Incorporadora',
      component: Step1
    },
    {
      id: 2,
      label: 'Procuradores',
      component: Step3
    },
    {
      id: 3,
      label: 'Final',
      component: Step4,
      disableContinueLater: true
    }
  ]
  const handleSave = () => {
    sessionStorage.setItem('CreateCompanyForm', JSON.stringify(data))
  }

  const isUpdatePage = Object.keys(company).length

  const onFinish = React.useCallback(async () => {
    try {
      const company = await createOrUpdateCompany()

      // If creating new company (not updating), show context switch prompt
      if (!isUpdatePage && company.id) {
        setCreatedCompanyId(company.id)
        setShowContextModal(true)
        return // Don't redirect yet
      }

      // If updating, just redirect back
      history.push('/companies')
    } catch {
      history.push('/companies')
      setErrorMessage('Não foi possível cadastrar/editar a Incorporadora.')
    }
  }, [data, isUpdatePage])

  const createOrUpdateCompany = async () => {
    return isUpdatePage ? updateCompany() : createCompany()
  }

  const updateCompany = () => {
    return handleUpdateCompany.editById(
      data as Required<ICreateCompanyForm & { id: string }>
    )
  }

  const createCompany = () => {
    return handleCreateCompany.createOne(data as Required<ICreateCompanyForm>)
  }

  React.useEffect(() => {
    if (errorMessage) {
      addToast(errorMessage, {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }, [errorMessage])

  const handleSwitchContext = async () => {
    if (selectContext && createdCompanyId && user) {
      try {
        // First, refresh the user's contexts list from the backend
        const { AxiosHttpClient } = await import(
          '../../../infra/http/axiosHttpClient'
        )
        const { URL_API } = await import('../../../services/api_url')
        const axiosHttpClient = new AxiosHttpClient(user.token)

        console.log('[CREATE COMPANY] Fetching updated contexts list...')
        const contextsResponse = await axiosHttpClient.get({
          url: `${URL_API}/auth/contexts`
        })

        console.log(
          '[CREATE COMPANY] Contexts refreshed:',
          contextsResponse.length
        )

        // Update user object with new contexts list
        // (o HttpClient não lança em erro: devolve o corpo do erro, que não
        // pode virar user.contexts — o ContextSelector faz contexts.map)
        const updatedUser = {
          ...user,
          contexts: Array.isArray(contextsResponse)
            ? contextsResponse
            : user.contexts
        }
        localStorage.setItem('user', JSON.stringify(updatedUser))

        // Now switch to the new company context
        await selectContext(createdCompanyId, updatedUser)

        addToast('Contexto alterado com sucesso!', {
          appearance: 'success',
          autoDismiss: true
        })
        window.location.reload() // Reload to refresh all data with new context
      } catch (error) {
        console.error('[CREATE COMPANY] Error switching context:', error)
        addToast('Erro ao alterar contexto', {
          appearance: 'error',
          autoDismiss: true
        })
        history.push('/companies')
      }
    }
  }

  const handleSkipContextSwitch = async () => {
    console.log('[CREATE COMPANY] handleSkipContextSwitch called')
    setShowContextModal(false)

    if (user) {
      try {
        console.log(
          '[CREATE COMPANY] User token:',
          user.token?.substring(0, 20)
        )

        // Wait a bit to ensure transaction is committed
        await new Promise((resolve) => setTimeout(resolve, 500))

        // Refresh contexts list even if user doesn't switch
        const { AxiosHttpClient } = await import(
          '../../../infra/http/axiosHttpClient'
        )
        const { URL_API } = await import('../../../services/api_url')
        const axiosHttpClient = new AxiosHttpClient(user.token)

        console.log(
          '[CREATE COMPANY] Refreshing contexts list (skip switch)...'
        )
        console.log('[CREATE COMPANY] Calling:', `${URL_API}/auth/contexts`)

        const contextsResponse = await axiosHttpClient.get({
          url: `${URL_API}/auth/contexts`
        })

        console.log('[CREATE COMPANY] Raw response:', contextsResponse)
        console.log(
          '[CREATE COMPANY] Is array?',
          Array.isArray(contextsResponse)
        )
        console.log('[CREATE COMPANY] Response type:', typeof contextsResponse)
        console.log(
          '[CREATE COMPANY] Response length:',
          contextsResponse?.length
        )

        // Update user object with new contexts list
        const updatedUser = {
          ...user,
          contexts: Array.isArray(contextsResponse)
            ? contextsResponse
            : user.contexts
        }

        console.log('[CREATE COMPANY] Updated user object:', updatedUser)
        console.log(
          '[CREATE COMPANY] Contexts in updated user:',
          updatedUser.contexts
        )

        localStorage.setItem('user', JSON.stringify(updatedUser))

        console.log('[CREATE COMPANY] Contexts list updated, reloading...')

        // Reload page to apply the new contexts
        window.location.reload()
      } catch (error) {
        console.error('[CREATE COMPANY] Error refreshing contexts:', error)
        console.error(
          '[CREATE COMPANY] Error details:',
          JSON.stringify(error, null, 2)
        )
        if (error?.response) {
          console.error(
            '[CREATE COMPANY] Response status:',
            error.response.status
          )
          console.error('[CREATE COMPANY] Response data:', error.response.data)
        }
        history.push('/companies')
      }
    } else {
      console.log('[CREATE COMPANY] No user found, redirecting to /companies')
      history.push('/companies')
    }
  }

  return (
    <>
      <Modal show={showContextModal} onHide={handleSkipContextSwitch} centered>
        <Modal.Header closeButton>
          <Modal.Title>Empresa criada com sucesso!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Deseja mudar para o contexto desta empresa agora?</p>
          <p className="text-muted">
            <small>
              Você poderá acessar esta empresa a qualquer momento através do
              seletor de contexto.
            </small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleSkipContextSwitch}>
            Não
          </Button>
          <Button variant="primary" onClick={handleSwitchContext}>
            Sim, mudar contexto
          </Button>
        </Modal.Footer>
      </Modal>

      <Row>
        <Col>
          <Card>
            <Card.Header>
              <Title>
                {isUpdatePage ? 'Editar' : 'Cadastrar'} Incorporadora
              </Title>
            </Card.Header>
            <Card.Body>
              <CreateCompanyContext.Provider value={{ data, setData }}>
                <FormWizard
                  canClickOnSteps={!!isUpdatePage}
                  onSaveDraft={handleSave}
                  steps={steps}
                  finishOnStep={3}
                  onFinish={onFinish}
                />
              </CreateCompanyContext.Provider>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}
